import React from 'react';
import PropTypes from 'prop-types';
import { PlusOutlined } from '@ant-design/icons';
import { Radio, Menu, Icon, Button, Switch } from 'antd';

import { propertyListingForm, platformEntity, entityAction, propertyRankType } from '~constants';
import { bookingJourneyType } from 'common/constants/booking';
import { extractSearchParams } from '~helpers/history';
import showElementByAuth, { isLandlordRole, isContentSpecialistRole } from '~helpers/auth';
import LandlordNamesFilter from './landlord-name-filter';
import TextSelectFilter from './select-filter';
import { Sort } from 'common/components/svgs';
import NewSearch from 'common/components/new-search';
import { displayLocaleOptions } from '~constants/property-field-options';

const RadioButton = Radio.Button;
const RadioGroup = Radio.Group;

export default class PropertyStatusFilter extends React.Component {
  constructor(props) {
    super(props);
    this.statusList = ['all', 'new', 'editing', 'published', 'unpublished'];
    this.statusListWithOutNew = ['all', 'editing', 'published', 'unpublished'];
    this.tagList = ['all', 'sold_out', 'rejected', 'poor_details'];
    this.popularCountriesList = ['all', 'uk', 'au', 'us'];
    this.sortList = ['updated_at', 'name'];
    this.state = {
      showSelectedOption:
        !!props?.filters?.countrySlug && props?.filters?.countrySlug !== 'all' ? true : false,
    };
  }

  getCountryMenu = () => (
    // TODO: wait platform ready
    <Menu onClick={() => {}}>
      <For each="country" of={this.popularCountriesList}>
        <Menu.Item key={country} onClick={this.handleSelectCountryFilter}>
          {this.props.t(`cms.property_status_filter.countries_filter.label.${country}`)}
        </Menu.Item>
      </For>
    </Menu>
  );

  getSortMenu = () => (
    <Menu onClick={() => {}}>
      <For each="sort" of={this.sortList}>
        <Menu.Item key={sort} onClick={this.handleSelectSortFilter}>
          {this.props.t(`cms.property_status_filter.sort_filter.label.${sort}`)}
        </Menu.Item>
      </For>
    </Menu>
  );

  getTagMenu = () => (
    <Menu onClick={() => {}}>
      <For each="tag" of={this.tagList}>
        <Menu.Item key={tag} onClick={this.handleSelectTagFilter}>
          {this.props.t(`cms.property_status_filter.tag_filter.label.${tag}`)}
        </Menu.Item>
      </For>
    </Menu>
  );

  handleChangeStatus = e => {
    let value = e.target.value.toUpperCase().replace('-', '_');
    if (Object.values(propertyListingForm.statuses).indexOf(value) === -1) {
      value = null;
    }

    this.props.onChange({ statuses: value });
  };

  handleSelectTagFilter = ({ key }) => {
    let value = key;
    if (Object.values(['sold_out', 'rejected', 'poor_details']).indexOf(key) === -1) {
      value = null;
    }

    this.props.onChange({ tag: value });
  };

  handleSelectCountryFilter = key => {
    let value = key;
    if (Object.values(['uk', 'au', 'us']).indexOf(key) === -1) {
      value = null;
    }

    this.props.onChange({ countrySlug: value });
  };

  handleTypeFilter = values => {
    this.props.onChange({ bookingJourneys: values });
  };

  handleDisplaySettingsFilter = values => {
    this.props.onChange({ displaySettings: values });
  };

  handleOnlyIncludeDisplaySetting = (bool, list) => {
    this.props.onChange({ onlyIncludeDisplaySetting: bool, displaySettings: list });
  };

  handleRankingTypeFilter = values => {
    this.props.onChange({ rankTypeCN: values.rankTypeCN, rankTypeROW: values.rankTypeROW });
  };

  handleSelectSortFilter = key => this.props.onChange({ sort: key });

  handlelandlordIdsFilter = ids => this.props.onChange({ landlordIds: ids });

  getStatusFilterList = () =>
    isLandlordRole() || isContentSpecialistRole() ? this.statusListWithOutNew : this.statusList;

  setBookTypeFilters = data => {
    let newData = [];
    if (!!data && typeof data === 'string') {
      newData = data.split();
    }
    if (!!data && Array.isArray(data)) {
      newData = data.concat();
    }
    return newData;
  };
  deletedBookFilter = (filter, defaultBookTypeFilters) => {
    let newBookTypeFilters = defaultBookTypeFilters;
    const index = defaultBookTypeFilters.indexOf(filter);
    if (index > -1) {
      newBookTypeFilters.splice(index, 1);
    }

    this.handleTypeFilter(newBookTypeFilters);
  };
  resetBookFilter = () => {
    this.handleTypeFilter([]);
  };

  render() {
    const { filters, t } = this.props;
    const { type } = extractSearchParams(filters.search);
    const SortsTypes = [
      { label: this.props.t('cms:properties_sort_by_time'), value: 'updated_at' },
      { label: this.props.t('cms:properties_sort_by_name'), value: 'name' },
    ];
    const bookingTypes = [
      {
        label: t(`properties_booking_type_${bookingJourneyType.MANUAL.toLowerCase()}`),
        value: bookingJourneyType.MANUAL,
      },
      {
        label: t(`properties_booking_type_${bookingJourneyType.SEMI_AUTOMATIC.toLowerCase()}`),
        value: bookingJourneyType.SEMI_AUTOMATIC,
      },
      {
        label: t(`properties_booking_type_${bookingJourneyType.FULL_AUTOMATIC.toLowerCase()}`),
        value: bookingJourneyType.FULL_AUTOMATIC,
      },
      {
        label: t(`properties_booking_type_${bookingJourneyType.INSTANT_BOOK.toLowerCase()}`),
        value: bookingJourneyType.INSTANT_BOOK,
      },
      {
        label: t(`properties_booking_type_${bookingJourneyType.LEAD_GENERATION.toLowerCase()}`),
        value: bookingJourneyType.LEAD_GENERATION,
      },
    ];

    const rankingTypes = [
      {
        label: t(`properties_ranking_type_${propertyRankType.STAR.toLowerCase()}`),
        value: propertyRankType.STAR,
      },
      {
        label: t(`properties_ranking_type_${propertyRankType.NORMAL.toLowerCase()}`),
        value: propertyRankType.NORMAL,
      },
      {
        label: t(`properties_ranking_type_${propertyRankType.BLACKSHEEP.toLowerCase()}`),
        value: propertyRankType.BLACKSHEEP,
      },
    ];

    const popularCountriesList = [
      {
        label: this.props.t('cms.property_status_filter.countries_filter.label.all'),
        value: 'all',
      },
      { label: this.props.t('cms.property_status_filter.countries_filter.label.uk'), value: 'uk' },
      { label: this.props.t('cms.property_status_filter.countries_filter.label.au'), value: 'au' },
      { label: this.props.t('cms.property_status_filter.countries_filter.label.us'), value: 'us' },
    ];

    const displaySettingOptions = displayLocaleOptions.map(option => ({
      label: option.name,
      value: option.value.toUpperCase().replace('-', '_'),
    }));

    const bookTypeFilter = this.setBookTypeFilters(filters.bookingJourneys);
    return (
      <div className="property-status-filter">
        <RadioGroup defaultValue="all" onChange={this.handleChangeStatus}>
          <For each="status" of={this.getStatusFilterList()}>
            <RadioButton
              value={status}
              key={status}
              checked={
                filters.statuses === status.toUpperCase().replace('-', '_') ||
                (status === 'all' && !filters.statuses)
              }
            >
              {this.props.t(`cms.property_status_filter.tab.label.${status.replace('-', '_')}`)}
            </RadioButton>
          </For>
        </RadioGroup>

        <div className="property-status-filter__filter-container">
          <h1 className="table-header__heading">
            {this.props.t('cms.table.header.text.num_properties', { num: this.props.total })}
          </h1>

          <If
            condition={showElementByAuth(platformEntity.PROPERTIES_PROPERTIES, entityAction.CREATE)}
          >
            <div className="table-header__button-container">
              <If condition={filters.statuses === 'PUBLISHED'}>
                <Button
                  type="primary"
                  disabled={this.props.selectedAmount === 0}
                  onClick={() => this.props.bulkOperation('unpublished')}
                >
                  {this.props.t('cms.properties.btn.unpublished')}
                </Button>
              </If>

              <Button onClick={this.props.addBtn}>
                <PlusOutlined className="table-list__icon-plus" />
                {this.props.t('cms.properties.create.name')}
              </Button>
            </div>
          </If>
        </div>
        <div className="property-status-filter__filter-bar">
          <If
            condition={
              (!type || ['citySlug', 'countrySlug'].indexOf(type) === -1) && !isLandlordRole()
            }
          >
            <div className="property-status-filter__country-filter">
              {/* contry filter */}
              <TextSelectFilter
                value={filters?.countrySlug ? filters.countrySlug : 'all'}
                options={popularCountriesList}
                btnText={this.props.t('cms:cms.table.column.country')}
                onSelect={item => {
                  if (item === 'all') {
                    this.setState({ showSelectedOption: false });
                  } else {
                    this.setState({ showSelectedOption: true });
                  }
                  this.handleSelectCountryFilter(item);
                }}
                showSelectedOption={this.state.showSelectedOption}
                noBtnBorder
                hasOutlinedIcon={false}
              />
            </div>
          </If>
          {/* landlord name filter */}
          <LandlordNamesFilter
            onChange={ids => {
              this.handlelandlordIdsFilter(ids);
            }}
            selectedIds={filters.landlordIds}
          />
          {/* BookType filter */}
          <NewSearch
            key="bookType"
            options={bookingTypes}
            onChange={items => {
              this.handleTypeFilter(items);
            }}
            placeholderInfo={{
              title: this.props.t('cms:properties_booking_type_title'),
              filteredTitle: this.props.t('cms:properties_booking_type_title'),
            }}
            defaultCheckedFilters={
              typeof filters.bookingJourneys === 'string'
                ? [filters.bookingJourneys]
                : filters.bookingJourneys || []
            }
            type="noBorder"
            noSearch
          />
          {/* RankType Filter */}
          <NewSearch
            key="rankingType"
            className="property-status-filter__rankType-filter"
            options={[
              { title: 'CN:', key: 'rankTypeCN', options: rankingTypes },
              { title: 'ROW:', key: 'rankTypeROW', options: rankingTypes },
            ]}
            onChange={items => {
              this.handleRankingTypeFilter(items);
            }}
            placeholderInfo={{
              title: this.props.t('cms:properties_ranking_type_title'),
              filteredTitle: this.props.t('cms:properties_ranking_type_title'),
            }}
            defaultCheckedFilters={{
              ...(filters.rankTypeCN ? { rankTypeCN: filters.rankTypeCN } : {}),
              ...(filters.rankTypeROW ? { rankTypeROW: filters.rankTypeROW } : {}),
            }}
            type="noBorder"
            noSearch
            isMultiSelect={false}
            use="radioGroup"
          />
          {/* Display locale filter */}
          <NewSearch
            key="displaySettings"
            options={displaySettingOptions}
            onChange={this.handleDisplaySettingsFilter}
            placeholderInfo={{
              title: this.props.t('cms:properties_display_settings_title'),
              filteredTitle: this.props.t('cms:properties_display_settings_title'),
            }}
            defaultCheckedFilters={
              typeof filters.displaySettings === 'string'
                ? [filters.displaySettings]
                : filters.displaySettings || []
            }
            type="noBorder"
            noSearch
            isMultiSelect={!(filters.onlyIncludeDisplaySetting === 'true')}
            customHeader={{
              canReset: filters.onlyIncludeDisplaySetting === 'true',
              onConfirm: ({ customHeaderData, checkedList }) => {
                this.handleOnlyIncludeDisplaySetting(customHeaderData, checkedList);
              },
              onReset: () => {
                this.handleOnlyIncludeDisplaySetting(false);
              },
              element: ({ onChange, setMultiSelect, clearSelected }) => (
                <div className="property-status-filter__custom-display-locale-header">
                  <label htmlFor="locale-filter">
                    {this.props.t('properties_display_settings_switch')}
                  </label>
                  <Switch
                    id="locale-filter"
                    defaultChecked={filters.onlyIncludeDisplaySetting === 'true'}
                    onChange={v => {
                      onChange(v);
                      setMultiSelect(!v);
                      v && clearSelected();
                    }}
                  />
                </div>
              ),
            }}
          />

          <span
            style={{
              width: 1,
              height: 20,
              background: 'rgba(0, 0, 0, 0.06)',
              display: 'inline-block',
              verticalAlign: -5,
              marginLeft: 16,
              marginRight: 16,
            }}
          />
          {/* sort by name/update at */}
          <TextSelectFilter
            value={filters?.sort ? filters.sort : 'updated_at'}
            options={SortsTypes}
            btnText={this.props.t('cms:properties_sort_by_time')}
            filterIcon={<Sort className="property_status_sort-svg" />}
            onSelect={item => {
              this.handleSelectSortFilter(item);
            }}
            showSelectedOption
            noBtnBorder
            hasOutlinedIcon={false}
            noHighLight={true}
          />
        </div>
        <If condition={!!bookTypeFilter.length}>
          <div className="bookingType-sub-filterbox">
            <div className="bookingType-subfilter-title">
              {this.props.t('cms:properties_booking_type_title')}:
            </div>
            <For each="bookTypeItem" of={bookTypeFilter} index="index">
              <button
                className="bookType-subfilter-btn"
                key={bookTypeItem}
                onClick={() => {
                  this.deletedBookFilter(bookTypeItem, bookTypeFilter);
                }}
              >
                {bookingTypes?.find(item => item?.value === bookTypeItem)?.label}
                <Icon type="close" className="bookingType-subfilter-icon" />
              </button>
            </For>
            <div className=" bookingType-reset-btn" onClick={this.resetBookFilter}>
              {this.props.t('cms:properties_reset_filter')}
            </div>
          </div>
        </If>

        <If condition={this.props.selectedAmount}>
          <div className="property-status-filter__select-quick-menu">
            <span className="property-status-filter__select-quick-menu__des">
              <span className="property-status-filter__select-quick-menu__des--important">
                {`${this.props.selectedAmount} properties `}
              </span>
              on this page are selected.
            </span>
            <span
              className="property-status-filter__select-quick-menu__link"
              onClick={() => this.props.bulkOperation('selectAll')}
            >
              Select All in Current Page
            </span>
            <span
              className="property-status-filter__select-quick-menu__link"
              onClick={() => this.props.bulkOperation('unselectAll')}
            >
              Unselect All
            </span>
          </div>
        </If>
      </div>
    );
  }
}

PropertyStatusFilter.propTypes = {
  t: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    statuses: PropTypes.string,
  }),
  addBtn: PropTypes.func.isRequired,
  bulkOperation: PropTypes.func.isRequired,
  selectedAmount: PropTypes.number.isRequired,
};

PropertyStatusFilter.defaultProps = {
  t: () => {},
  onChange: () => {},
  total: 0,
  filters: {
    statuses: null,
  },
  addBtn: () => {},
  bulkOperation: () => {},
  selectedAmount: 0,
};
